// frontend/src/components/MarineMap.tsx
// Версия: 3.0.0 (2025-12-13)
//
// ФУНКЦИОНАЛ:
// - Двухслойная карта: РФ (русские названия), мир (английские)
// - OpenSeaMap: морские навигационные знаки
// - ESRI Ocean: батиметрия (глубины океана)
// - Спутниковые снимки (опционально)
// - Popup с информацией о сигнале при клике
// - Переключатели слоёв
// - Легенда с классификацией сигналов ССТО
//
// КЛАССИФИКАЦИЯ СИГНАЛОВ:
// 🔴 DISTRESS    — Реальная тревога (немедленная реакция)
// 🟠 TRAINING    — Тренировочная тревога (согласована)
// 🔵 TEST        — Плановый тест по заявке
// 🟡 UNMATCHED   — Ожидает проверки (нет заявки)
// 🟢 CONFIRMED   — Тест подтверждён
// ⚫ REJECTED    — Отклонён/ложный

import React, { useRef, useEffect, useState, useCallback, useMemo } from 'react';
import Map from 'ol/Map';
import View from 'ol/View';
import TileLayer from 'ol/layer/Tile';
import OSM from 'ol/source/OSM';
import XYZ from 'ol/source/XYZ';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import { Point } from 'ol/geom';
import Feature from 'ol/Feature';
import { Style, Text, Fill, Stroke, Circle as CircleStyle } from 'ol/style';
import { fromLonLat, transformExtent } from 'ol/proj';
import Overlay from 'ol/Overlay';
import {
  Box,
  FormControlLabel,
  Switch,
  Paper,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  Divider,
  Button,
  TextField,
} from '@mui/material';
import { CalendarMonth } from '@mui/icons-material';
import { getShiftPeriod } from '../utils/shiftPeriod';
import 'ol/ol.css';

// =============================================================================
// ИНТЕРФЕЙСЫ
// =============================================================================

interface Signal {
  id: number | string;
  vessel_name?: string;
  mmsi?: string;
  imo_number?: string;
  terminal_number?: string;
  signal_type?: 'DISTRESS' | 'TRAINING' | 'TEST' | 'SSAS_TEST' | 'REAL_ALERT' | 'SSAS_ALERT';
  status?: 'UNMATCHED' | 'MATCHED' | 'CONFIRMED' | 'REJECTED' | 'PENDING';
  coordinates?: {
    lat?: number;
    lon?: number;
    lng?: number;
    latitude?: number;
    longitude?: number;
  };
  latitude?: number;
  longitude?: number;
  received_at?: string;
  created_at?: string;
  request_id?: number;
}

interface MarineMapProps {
  signals: Signal[];
  center?: [number, number];
  zoom?: number;
  highlightSignalId?: number | string;
  skipAutoFit?: boolean; // Не центрировать автоматически на все сигналы
}

// =============================================================================
// КОНСТАНТЫ И ХЕЛПЕРЫ
// =============================================================================

// Границы РФ для маски русского слоя (EPSG:4326)
const RUSSIA_EXTENT = [19.25, 41.19, 180, 81.86];

// Центр по умолчанию: Владивосток (морской регион РФ)
const DEFAULT_CENTER: [number, number] = [131.9, 43.1];
const DEFAULT_ZOOM = 5;

const toUtcDateTimeLocalValue = (date: Date): string => date.toISOString().slice(0, 16);

const parseUtcDateTimeLocal = (value?: string): Date | null => {
  if (!value) return null;
  const normalized = value.endsWith('Z') ? value : `${value}${value.length === 16 ? ':00' : ''}Z`;
  const date = new Date(normalized);
  return Number.isNaN(date.getTime()) ? null : date;
};

const getCurrentDutyInputPeriod = (): { from: string; to: string } => {
  const shift = getShiftPeriod();
  return {
    from: toUtcDateTimeLocalValue(shift.start),
    to: toUtcDateTimeLocalValue(shift.end),
  };
};

const formatUtcPeriodValue = (value: string): string => value.replace('T', ' ');

// Цвета сигналов по типу и статусу
const SIGNAL_COLORS: Record<string, string> = {
  // По типу сигнала
  DISTRESS: '#F44336', // 🔴 Красный — реальная тревога
  REAL_ALERT: '#F44336', // 🔴 Красный — реальная тревога
  SSAS_ALERT: '#F44336', // 🔴 Красный — SSAS тревога
  TRAINING: '#FF9800', // 🟠 Оранжевый — тренировка
  TEST: '#2196F3', // 🔵 Синий — плановый тест
  SSAS_TEST: '#2196F3', // 🔵 Синий — плановый тест
  // По статусу
  UNMATCHED: '#FFC107', // 🟡 Жёлтый — ожидает проверки
  MATCHED: '#8BC34A', // 🟢 Светло-зелёный — сопоставлен (но не подтверждён)
  PENDING: '#9C27B0', // 🟣 Фиолетовый — в обработке
  CONFIRMED: '#4CAF50', // 🟢 Зелёный — подтверждён
  REJECTED: '#9E9E9E', // ⚫ Серый — отклонён
  NEW: '#03A9F4', // 🔵 Голубой — новый
  ERROR: '#E91E63', // 🔴 Розовый — ошибка
  // Дефолт
  DEFAULT: '#9E9E9E', // ⚫ Серый
};

// Русские названия для легенды и popup
const SIGNAL_LABELS: Record<string, string> = {
  // Типы сигналов
  DISTRESS: 'Реальная тревога',
  REAL_ALERT: 'Реальная тревога',
  SSAS_ALERT: 'SSAS Тревога',
  TRAINING: 'Тренировка',
  TEST: 'Тест по заявке',
  SSAS_TEST: 'Тест ССТО',
  // Статусы сигналов
  UNMATCHED: 'Ожидает проверки',
  MATCHED: 'Сопоставлен', // ✅ ДОБАВЛЕНО
  PENDING: 'В обработке', // ✅ ДОБАВЛЕНО
  CONFIRMED: 'Подтверждён',
  REJECTED: 'Отклонён',
  // Дополнительные статусы
  NEW: 'Новый', // ✅ ДОБАВЛЕНО
  ERROR: 'Ошибка', // ✅ ДОБАВЛЕНО
};

// Извлечение координат из разных форматов
const getCoordinates = (signal: Signal): [number, number] | null => {
  // Формат 1: coordinates.lat, coordinates.lon
  if (signal.coordinates?.lat !== undefined && signal.coordinates?.lon !== undefined) {
    return [signal.coordinates.lon, signal.coordinates.lat];
  }
  // Формат 2: coordinates.lat, coordinates.lng
  if (signal.coordinates?.lat !== undefined && signal.coordinates?.lng !== undefined) {
    return [signal.coordinates.lng, signal.coordinates.lat];
  }
  // Формат 3: coordinates.latitude, coordinates.longitude
  if (signal.coordinates?.latitude !== undefined && signal.coordinates?.longitude !== undefined) {
    return [signal.coordinates.longitude, signal.coordinates.latitude];
  }
  // Формат 4: плоские поля latitude, longitude
  if (signal.latitude !== undefined && signal.longitude !== undefined) {
    return [signal.longitude, signal.latitude];
  }
  return null;
};

// Определение цвета сигнала (приоритет: тип > статус)
const getSignalColor = (signal: Signal): string => {
  // БАГ 3 FIX: CONFIRMED всегда зелёный, независимо от signal_type
  if (signal.status === 'CONFIRMED') return SIGNAL_COLORS.CONFIRMED;

  // Сначала проверяем тип (DISTRESS важнее всего)
  if (signal.signal_type) {
    const typeColor = SIGNAL_COLORS[signal.signal_type];
    if (typeColor) return typeColor;
  }
  // Затем статус
  if (signal.status) {
    const statusColor = SIGNAL_COLORS[signal.status];
    if (statusColor) return statusColor;
  }
  return SIGNAL_COLORS.DEFAULT;
};

// Форматирование даты
const formatDate = (dateStr?: string): string => {
  if (!dateStr) return 'Н/Д';
  try {
    return new Date(dateStr).toLocaleString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return dateStr;
  }
};

// =============================================================================
// КОМПОНЕНТ
// =============================================================================

const MarineMap: React.FC<MarineMapProps> = ({
  signals,
  center = DEFAULT_CENTER,
  zoom = DEFAULT_ZOOM,
  highlightSignalId,
  skipAutoFit = false,
}) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<Map | null>(null);

  // Состояние переключателей слоёв
  const [showOpenSeaMap, setShowOpenSeaMap] = useState(true);
  const [showBathymetry, setShowBathymetry] = useState(false);
  const [showSignals, setShowSignals] = useState(true);
  const [baseLayer, setBaseLayer] = useState<'osm' | 'satellite'>('osm');

  // --------------------------------------------------------------------------
  // ДОБАВЛЕНО (ЗАДАЧА Б): фильтры периода
  // --------------------------------------------------------------------------
  const initialDutyPeriod = useMemo(() => getCurrentDutyInputPeriod(), []);
  const [dateFrom, setDateFrom] = useState<string>(initialDutyPeriod.from);
  const [dateTo, setDateTo] = useState<string>(initialDutyPeriod.to);
  const [currentDutyActive, setCurrentDutyActive] = useState(true);

  // Индексы слоёв
  const LAYER_INDICES = {
    ENGLISH_BASE: 0, // Английские названия (весь мир)
    RUSSIAN_OVERLAY: 1, // Русские названия (только РФ)
    SATELLITE: 2, // Спутник (альтернатива OSM)
    BATHYMETRY: 3, // ESRI Ocean батиметрия
    OPENSEAMAP: 4, // Морские знаки
    SIGNALS: 5, // Сигналы ССТО
  };

  // ==========================================================================
  // Фильтр периода дежурства: 05:00 UTC → 05:00 UTC
  // ==========================================================================

  const handleSetCurrentShift = () => {
    const period = getCurrentDutyInputPeriod();
    setDateFrom(period.from);
    setDateTo(period.to);
    setCurrentDutyActive(true);
  };

  const handleClearPeriod = () => {
    setDateFrom('');
    setDateTo('');
    setCurrentDutyActive(false);
  };

  const handleDateFromChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDateFrom(e.target.value);
    setCurrentDutyActive(false);
  };

  const handleDateToChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDateTo(e.target.value);
    setCurrentDutyActive(false);
  };

  const hasCoordinates = useCallback((s: Signal) => !!getCoordinates(s), []);

  const totalSignalsWithCoordinates = useMemo(() => {
    return signals.filter((s) => hasCoordinates(s)).length;
  }, [signals, hasCoordinates]);

  const currentDutyPeriodLabel = useMemo(() => {
    if (!currentDutyActive || !dateFrom || !dateTo) return '';
    return `${formatUtcPeriodValue(dateFrom)} UTC → ${formatUtcPeriodValue(dateTo)} UTC`;
  }, [currentDutyActive, dateFrom, dateTo]);

  const filteredSignals = useMemo(() => {
    let result = signals.filter((s) => hasCoordinates(s));

    if (dateFrom || dateTo) {
      const fromDate = parseUtcDateTimeLocal(dateFrom);
      const toDate = parseUtcDateTimeLocal(dateTo);

      result = result.filter((signal) => {
        const signalDate = new Date(signal.received_at || signal.created_at || '');
        if (isNaN(signalDate.getTime())) return true; // не режем сигналы без даты

        if (fromDate && signalDate < fromDate) return false;
        if (toDate && signalDate > toDate) return false;
        return true;
      });
    }

    return result;
  }, [signals, dateFrom, dateTo, hasCoordinates]);

  // ==========================================================================
  // ИНИЦИАЛИЗАЦИЯ КАРТЫ
  // ==========================================================================

  useEffect(() => {
    if (!mapRef.current || mapInstanceRef.current) return;

    // -------------------------------------------------------------------------
    // СЛОЙ 1: Базовая карта (английские названия, весь мир)
    // -------------------------------------------------------------------------
    const englishLayer = new TileLayer({
      source: new OSM(),
      visible: baseLayer === 'osm',
    });

    // -------------------------------------------------------------------------
    // СЛОЙ 2: Русские названия (только территория РФ, поверх английского)
    // -------------------------------------------------------------------------
    const russianLayer = new TileLayer({
      source: new XYZ({
        url: 'https://tile.openstreetmap.ru/tiles/russian/{z}/{x}/{y}.png',
        attributions: ['© OpenStreetMap contributors'],
        crossOrigin: 'anonymous',
      }),
      extent: transformExtent(RUSSIA_EXTENT, 'EPSG:4326', 'EPSG:3857'),
      visible: baseLayer === 'osm',
    });

    // -------------------------------------------------------------------------
    // СЛОЙ 3: Спутниковые снимки (альтернатива OSM)
    // -------------------------------------------------------------------------
    const satelliteLayer = new TileLayer({
      source: new XYZ({
        url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
        attributions: ['© Esri'],
        crossOrigin: 'anonymous',
      }),
      visible: baseLayer === 'satellite',
    });

    // -------------------------------------------------------------------------
    // СЛОЙ 4: ESRI Ocean Basemap (батиметрия — глубины океана)
    // -------------------------------------------------------------------------
    const bathymetryLayer = new TileLayer({
      source: new XYZ({
        url: 'https://server.arcgisonline.com/ArcGIS/rest/services/Ocean/World_Ocean_Base/MapServer/tile/{z}/{y}/{x}',
        attributions: ['© Esri Ocean'],
        crossOrigin: 'anonymous',
      }),
      visible: showBathymetry,
      opacity: 0.7,
    });

    // -------------------------------------------------------------------------
    // СЛОЙ 5: OpenSeaMap (морские навигационные знаки)
    // -------------------------------------------------------------------------
    const openSeaMapLayer = new TileLayer({
      source: new XYZ({
        url: 'https://tiles.openseamap.org/seamark/{z}/{x}/{y}.png',
        attributions: ['© OpenSeaMap contributors'],
        crossOrigin: 'anonymous',
      }),
      visible: showOpenSeaMap,
    });

    // -------------------------------------------------------------------------
    // СЛОЙ 6: Сигналы ССТО (векторный)
    // -------------------------------------------------------------------------
    const signalsSource = new VectorSource();
    const signalsLayer = new VectorLayer({
      source: signalsSource,
      style: (feature) => {
        const signal = feature.get('signal') as Signal;
        const color = getSignalColor(signal);
        const isHighlighted = highlightSignalId && signal.id === highlightSignalId;
        const isEmergency =
          signal.signal_type === 'DISTRESS' ||
          signal.signal_type === 'REAL_ALERT' ||
          signal.signal_type === 'SSAS_ALERT';

        return new Style({
          image: new CircleStyle({
            radius: isHighlighted ? 12 : isEmergency ? 10 : 8,
            fill: new Fill({ color: color }),
            stroke: new Stroke({
              color: isHighlighted ? '#000' : '#fff',
              width: isHighlighted ? 3 : 2,
            }),
          }),
          text: new Text({
            text: signal.vessel_name || signal.terminal_number || 'Неизвестно',
            offsetY: -18,
            font: isHighlighted ? 'bold 13px Arial' : '12px Arial',
            fill: new Fill({ color: '#000' }),
            stroke: new Stroke({ color: '#fff', width: 3 }),
          }),
        });
      },
      visible: showSignals,
    });

    // -------------------------------------------------------------------------
    // СОЗДАНИЕ КАРТЫ
    // -------------------------------------------------------------------------
    const map = new Map({
      target: mapRef.current,
      layers: [
        englishLayer, // 0
        russianLayer, // 1
        satelliteLayer, // 2
        bathymetryLayer, // 3
        openSeaMapLayer, // 4
        signalsLayer, // 5
      ],
      view: new View({
        center: fromLonLat(center),
        zoom: zoom,
        maxZoom: 18,
        minZoom: 2,
      }),
    });

    // -------------------------------------------------------------------------
    // POPUP
    // -------------------------------------------------------------------------
    if (popupRef.current) {
      const popup = new Overlay({
        element: popupRef.current,
        positioning: 'bottom-center',
        stopEvent: false,
        offset: [0, -15],
      });
      map.addOverlay(popup);

      // Обработчик клика — показать popup
      map.on('click', (evt) => {
        const feature = map.forEachFeatureAtPixel(evt.pixel, (f) => f);

        if (feature && popupRef.current) {
          const signal = feature.get('signal') as Signal;
          const coords = getCoordinates(signal);

          popupRef.current.innerHTML = `
            <div style="
              background: white;
              padding: 12px 16px;
              border-radius: 8px;
              box-shadow: 0 4px 12px rgba(0,0,0,0.3);
              min-width: 280px;
              font-size: 13px;
              line-height: 1.5;
            ">
              <div style="
                font-size: 15px;
                font-weight: bold;
                margin-bottom: 8px;
                color: ${getSignalColor(signal)};
              ">
                ${signal.vessel_name || 'Неизвестное судно'}
              </div>
              <hr style="margin: 8px 0; border: none; border-top: 1px solid #eee;"/>
              <div><b>MMSI:</b> ${signal.mmsi || 'Н/Д'}</div>
              <div><b>IMO:</b> ${signal.imo_number || 'Н/Д'}</div>
              <div><b>Терминал:</b> ${signal.terminal_number || 'Н/Д'}</div>
              <div><b>Тип:</b> <span style="color: ${getSignalColor(signal)}">
                ${SIGNAL_LABELS[signal.signal_type || ''] || signal.signal_type || 'Н/Д'}
              </span></div>
              <div><b>Статус:</b> ${SIGNAL_LABELS[signal.status || ''] || signal.status || 'Н/Д'}</div>
              ${coords ? `<div><b>Координаты:</b> ${coords[1].toFixed(4)}°, ${coords[0].toFixed(4)}°</div>` : ''}
              <div><b>Время:</b> ${formatDate(signal.received_at || signal.created_at)}</div>
              ${signal.request_id ? `<div><b>Заявка:</b> #${signal.request_id}</div>` : ''}
            </div>
          `;
          popup.setPosition(evt.coordinate);
        } else {
          popup.setPosition(undefined);
        }
      });

      // Изменение курсора при наведении
      map.on('pointermove', (evt) => {
        const pixel = map.getEventPixel(evt.originalEvent);
        const hit = map.hasFeatureAtPixel(pixel);
        if (mapRef.current) {
          mapRef.current.style.cursor = hit ? 'pointer' : '';
        }
      });
    }

    mapInstanceRef.current = map;

    return () => {
      map.setTarget(undefined);
      mapInstanceRef.current = null;
    };
  }, []); // Только один раз при монтировании

  // ==========================================================================
  // ОБНОВЛЕНИЕ СИГНАЛОВ
  // ==========================================================================

  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map) return;

    const signalsLayer = map.getLayers().item(LAYER_INDICES.SIGNALS) as VectorLayer<VectorSource>;
    if (!signalsLayer) return;

    const source = signalsLayer.getSource();
    if (!source) return;

    source.clear();

    filteredSignals.forEach((signal) => {
      const coords = getCoordinates(signal);
      if (!coords) return;

      const feature = new Feature({
        geometry: new Point(fromLonLat(coords)),
        signal: signal,
      });
      source.addFeature(feature);
    });

    // Автоцентрирование если есть сигналы
    if (!skipAutoFit && filteredSignals.length > 0 && source.getFeatures().length > 0) {
      const extent = source.getExtent();
      if (extent && extent[0] !== Infinity) {
        map.getView().fit(extent, {
          padding: [50, 50, 50, 50],
          maxZoom: 12,
          duration: 500,
        });
      }
    }
  }, [filteredSignals, highlightSignalId, skipAutoFit]);

  // ==========================================================================
  // ОБНОВЛЕНИЕ ВИДИМОСТИ СЛОЁВ
  // ==========================================================================

  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map) return;

    const layers = map.getLayers();

    // Базовые слои (OSM vs Satellite)
    layers.item(LAYER_INDICES.ENGLISH_BASE)?.setVisible(baseLayer === 'osm');
    layers.item(LAYER_INDICES.RUSSIAN_OVERLAY)?.setVisible(baseLayer === 'osm');
    layers.item(LAYER_INDICES.SATELLITE)?.setVisible(baseLayer === 'satellite');

    // Дополнительные слои
    layers.item(LAYER_INDICES.BATHYMETRY)?.setVisible(showBathymetry);
    layers.item(LAYER_INDICES.OPENSEAMAP)?.setVisible(showOpenSeaMap);
    layers.item(LAYER_INDICES.SIGNALS)?.setVisible(showSignals);
  }, [baseLayer, showBathymetry, showOpenSeaMap, showSignals]);

  // ==========================================================================
  // ОБНОВЛЕНИЕ ЦЕНТРА И ЗУМА
  // ==========================================================================

  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map) return;

    map.getView().animate({
      center: fromLonLat(center),
      zoom: zoom,
      duration: 500,
    });
  }, [center, zoom]);

  // ==========================================================================
  // РЕНДЕР
  // ==========================================================================

  return (
    <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
      {/* ------------------------------------------------------------------- */}
      {/* ДОБАВЛЕНО (ЗАДАЧА Б): Панель фильтров периода НАД картой            */}
      {/* ------------------------------------------------------------------- */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1,
          mb: 1.5,
          p: 1.5,
          bgcolor: 'background.paper',
          borderRadius: 1,
          border: '1px solid',
          borderColor: 'divider',
          alignItems: 'center',
        }}
      >
        <Button
          variant={currentDutyActive ? 'contained' : 'outlined'}
          color="primary"
          startIcon={<CalendarMonth />}
          onClick={handleSetCurrentShift}
          size="small"
          title="Установить период текущего дежурства (05:00 UTC → 05:00 UTC)"
        >
          ТЕКУЩЕЕ ДЕЖУРСТВО
        </Button>

        <TextField
          label="С (UTC)"
          type="datetime-local"
          value={dateFrom}
          onChange={handleDateFromChange}
          InputLabelProps={{ shrink: true }}
          size="small"
          sx={{ minWidth: 220 }}
        />

        <TextField
          label="По (UTC)"
          type="datetime-local"
          value={dateTo}
          onChange={handleDateToChange}
          InputLabelProps={{ shrink: true }}
          size="small"
          sx={{ minWidth: 220 }}
        />

        {(dateFrom || dateTo) && (
          <>
            <Button variant="outlined" color="secondary" onClick={handleClearPeriod} size="small">
              Сбросить период
            </Button>

            <Typography variant="body2" color="textSecondary" sx={{ ml: 1 }}>
              Показано: {filteredSignals.length} из {totalSignalsWithCoordinates}
              {currentDutyPeriodLabel ? ` • Дежурство: ${currentDutyPeriodLabel}` : ''}
            </Typography>
          </>
        )}
      </Box>

      {/* Контейнер карты */}
      <div ref={mapRef} style={{ width: '100%', height: '600px' }} />

      {/* Popup контейнер */}
      <div ref={popupRef} style={{ position: 'absolute' }} />

      {/* ===================================================================== */}
      {/* ПАНЕЛЬ УПРАВЛЕНИЯ СЛОЯМИ (правый верхний угол) */}
      {/* ===================================================================== */}
      <Paper
        sx={{
          position: 'absolute',
          top: 10,
          right: 10,
          p: 1.5,
          borderRadius: 2,
          boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
          background: 'rgba(255, 255, 255, 0.95)',
          minWidth: 180,
        }}
      >
        <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
          🗺️ Базовая карта
        </Typography>
        <ToggleButtonGroup
          value={baseLayer}
          exclusive
          onChange={(_, value) => value && setBaseLayer(value)}
          size="small"
          fullWidth
          sx={{ mb: 1 }}
        >
          <ToggleButton value="osm">OSM</ToggleButton>
          <ToggleButton value="satellite">🛰️ Спутник</ToggleButton>
        </ToggleButtonGroup>

        <Divider sx={{ my: 1 }} />

        <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
          📍 Слои
        </Typography>
        <FormControlLabel
          control={
            <Switch
              checked={showOpenSeaMap}
              onChange={(e) => setShowOpenSeaMap(e.target.checked)}
              size="small"
            />
          }
          label="🚢 Морские знаки"
          sx={{ display: 'block', ml: 0 }}
        />
        <FormControlLabel
          control={
            <Switch
              checked={showBathymetry}
              onChange={(e) => setShowBathymetry(e.target.checked)}
              size="small"
            />
          }
          label="🌊 Батиметрия"
          sx={{ display: 'block', ml: 0 }}
        />
        <FormControlLabel
          control={
            <Switch
              checked={showSignals}
              onChange={(e) => setShowSignals(e.target.checked)}
              size="small"
            />
          }
          label="📡 Сигналы ССТО"
          sx={{ display: 'block', ml: 0 }}
        />
      </Paper>

      {/* ===================================================================== */}
      {/* ЛЕГЕНДА (левый нижний угол) */}
      {/* ===================================================================== */}
      <Paper
        sx={{
          position: 'absolute',
          bottom: 10,
          left: 10,
          p: 1.5,
          borderRadius: 2,
          boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
          background: 'rgba(255, 255, 255, 0.95)',
          fontSize: '12px',
        }}
      >
        <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
          Сигналы ССТО
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{ width: 14, height: 14, bgcolor: SIGNAL_COLORS.DISTRESS, borderRadius: '50%' }}
            />
            <span>Реальная тревога</span>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{ width: 14, height: 14, bgcolor: SIGNAL_COLORS.TRAINING, borderRadius: '50%' }}
            />
            <span>Тренировка</span>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 14, height: 14, bgcolor: SIGNAL_COLORS.TEST, borderRadius: '50%' }} />
            <span>Тест по заявке</span>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{ width: 14, height: 14, bgcolor: SIGNAL_COLORS.UNMATCHED, borderRadius: '50%' }}
            />
            <span>Ожидает проверки</span>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{ width: 14, height: 14, bgcolor: SIGNAL_COLORS.MATCHED, borderRadius: '50%' }}
            />
            <span>Сопоставлен</span>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{ width: 14, height: 14, bgcolor: SIGNAL_COLORS.CONFIRMED, borderRadius: '50%' }}
            />
            <span>Подтверждён</span>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{ width: 14, height: 14, bgcolor: SIGNAL_COLORS.REJECTED, borderRadius: '50%' }}
            />
            <span>Отклонён</span>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default MarineMap;
